import { D6_SCALE } from '@/config/solana';

/** Outcome shares use the contract's D6 mint precision. */
export const OUTCOME_TOKEN_D6_DIVISOR: number = D6_SCALE;
const OUTCOME_TOKEN_D6_SCALE: bigint = BigInt(OUTCOME_TOKEN_D6_DIVISOR);
const TWO_DECIMAL_ROUNDING_D6: bigint = BigInt(10_000);
const SHARE_DECIMAL_SEPARATOR: string =
  new Intl.NumberFormat(undefined).formatToParts(1.1).find((part) => part.type === 'decimal')
    ?.value ?? '.';

/** Use two decimals unless that would hide a nonzero D6 share count. */
export function formatShares(amount: string | bigint): string {
  const value = parseShares(amount);
  const negative = value < BigInt(0);
  const magnitude = negative ? -value : value;
  const roundedMagnitude =
    ((magnitude + TWO_DECIMAL_ROUNDING_D6 / BigInt(2)) / TWO_DECIMAL_ROUNDING_D6) *
    TWO_DECIMAL_ROUNDING_D6;
  const displayMagnitude = roundedMagnitude === BigInt(0) ? magnitude : roundedMagnitude;
  const whole = displayMagnitude / OUTCOME_TOKEN_D6_SCALE;
  const fraction = (displayMagnitude % OUTCOME_TOKEN_D6_SCALE)
    .toString()
    .padStart(6, '0')
    .slice(0, roundedMagnitude === BigInt(0) ? 6 : 2)
    .replace(/0+$/, '');
  const unsigned =
    fraction.length === 0
      ? whole.toLocaleString()
      : `${whole.toLocaleString()}${SHARE_DECIMAL_SEPARATOR}${fraction}`;
  return negative ? `-${unsigned}` : unsigned;
}

/** Format every significant D6 decimal without converting the quote to a float. */
export function formatExactShares(amount: string | bigint): string {
  const value = parseShares(amount);
  const negative = value < BigInt(0);
  const magnitude = negative ? -value : value;
  const whole = magnitude / OUTCOME_TOKEN_D6_SCALE;
  const fraction = (magnitude % OUTCOME_TOKEN_D6_SCALE)
    .toString()
    .padStart(6, '0')
    .replace(/0+$/, '');
  const unsigned = fraction.length === 0 ? whole.toString() : `${whole.toString()}.${fraction}`;
  return negative ? `-${unsigned}` : unsigned;
}

function parseShares(amount: string | bigint): bigint {
  return typeof amount === 'bigint' ? amount : BigInt(amount.length === 0 ? '0' : amount);
}
